import {
  CompositePropagator,
  W3CBaggagePropagator,
  W3CTraceContextPropagator,
} from "@opentelemetry/core";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { XMLHttpRequestInstrumentation } from "@opentelemetry/instrumentation-xml-http-request";
import { resourceFromAttributes } from "@opentelemetry/resources";
import { BatchSpanProcessor } from "@opentelemetry/sdk-trace-base";
// Web OpenTelemetry initialisation (OTLP/HTTP)
import { WebTracerProvider, StackContextManager } from "@opentelemetry/sdk-trace-web";
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from "@opentelemetry/semantic-conventions";
import { ATTR_DEPLOYMENT_ENVIRONMENT_NAME } from "@opentelemetry/semantic-conventions/incubating";

let initialised = false;
let warnedNoEndpoint = false;

const createIgnoreUrls = (allowedUrls: string[]) => {
  if (!allowedUrls.length) {
    return undefined;
  }

  const escapedUrls = allowedUrls.map((url) => {
    return url.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  });
  const ignoreUrlsRegex = new RegExp(`^(?!(${escapedUrls.join("|")})).*`);
  return [ignoreUrlsRegex];
};

export interface InitialiseOtelOptions {
  endpoint: string | undefined;
  environment?: string;
  appVersion?: string;
  serviceName?: string;
  allowedUrls?: string[];
  batch?: {
    maxExportBatchSize?: number;
    scheduledDelayMillis?: number;
    maxQueueSize?: number;
    exportTimeoutMillis?: number;
  };
}

/** Initialise frontend OpenTelemetry SDK.
 * Call this as early as possible in the app entry (before router/render).
 * Safe to call multiple times; subsequent calls no-op.
 */
export function initialiseOtel(options?: InitialiseOtelOptions): void {
  if (initialised) return;

  const isTest = typeof process !== "undefined" && process.env.NODE_ENV === "test";
  if (isTest) return;

  const endpoint = options?.endpoint;
  if (!endpoint) {
    if (!warnedNoEndpoint) {
      console.info("[OTel] Skipping init: no OTLP HTTP endpoint configured");
      warnedNoEndpoint = true; // avoid repeated logs but allow later retries if endpoint appears
    }
    return;
  }

  try {
    const resource = resourceFromAttributes({
      [ATTR_SERVICE_NAME]: options?.serviceName || "cloud-ui",
      [ATTR_SERVICE_VERSION]: options?.appVersion || "unknown",
      [ATTR_DEPLOYMENT_ENVIRONMENT_NAME]: options?.environment || "unknown",
    });

    const exporter = new OTLPTraceExporter({ url: endpoint });
    // Force XHR instead of navigator.sendBeacon to avoid 'sendBeacon - cannot send' errors
    // when the browser declines beacon delivery (e.g., budget/CSP). This relies on an internal flag.
    try {
      (exporter as unknown as { _useXHR: boolean })._useXHR = true;
    } catch {
      // noop: exporter private flag not available in this version
    }
    const batchCfg = {
      maxExportBatchSize: options?.batch?.maxExportBatchSize ?? 100,
      scheduledDelayMillis: options?.batch?.scheduledDelayMillis ?? 500,
      maxQueueSize: options?.batch?.maxQueueSize ?? 1000,
      exportTimeoutMillis: options?.batch?.exportTimeoutMillis ?? 30000,
    };

    const provider = new WebTracerProvider({
      resource,
      spanProcessors: [new BatchSpanProcessor(exporter, batchCfg)],
    });
    provider.register({
      contextManager: new StackContextManager(),
      propagator: new CompositePropagator({
        propagators: [new W3CTraceContextPropagator(), new W3CBaggagePropagator()],
      }),
    });

    const ignoreUrls = createIgnoreUrls(options.allowedUrls || []);

    // Propagator configured via provider.register above
    registerInstrumentations({
      instrumentations: [
        new FetchInstrumentation({
          propagateTraceHeaderCorsUrls: options?.allowedUrls || [],
          ignoreUrls,
        }),
        new XMLHttpRequestInstrumentation({
          propagateTraceHeaderCorsUrls: options?.allowedUrls || [],
          ignoreUrls,
        }),
      ],
    });

    initialised = true; // mark only after successful setup

    console.info("[OTel] Initialised (OTLP/HTTP)");
  } catch (err) {
    console.warn("[OTel] initialisation skipped or failed:", err);
    initialised = false; // keep retryable on subsequent calls
  }
}
